import { memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useComponentCategories } from '../../../utils/useComponentCategories';
import { Slider } from '../../../Slider/Slider';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpCategories } from '../../../../modules/categories';
import { usePlaceholders } from '../../usePlaceholders';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { CategoryItem } from '../../common/CategoryItem/CategoryItem';
import { useCategoryItemTracking } from '../../common/useCategoryItemTracking';
import { useViewportType } from '../../../utils/useViewportType';
import { useComponentPopup } from '../../ComponentPopup/useComponentPopup';

import type { SvpCategorySliderComponent } from '@vgtv/api-client/lib/component/slider';
import type { FC } from 'react';

import * as styles from './CategoriesSlider.module.scss';

type Props = {
  componentConfig: SvpCategorySliderComponent;
  pageComponentIndex: number;
};

const CategoriesSliderBase: FC<Props> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { isFetched, visibleItemsCount } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchSvpCategories,
  });

  const { t } = useTranslation();

  const headerButtonLabel = t('see all');
  const { isDesktop } = useViewportType();

  const { id, header, type: componentType } = componentConfig;
  const { useLargeThumbnails, skipMetadata } = componentConfig.options;
  const displayLargeThumbnails = useLargeThumbnails && isDesktop;

  const categories = useComponentCategories(id);

  const placeholders = usePlaceholders(isFetched);

  const { setComponentPopupId } = useComponentPopup();

  const { trackCategoryClick, trackHeaderButtonClick } =
    useCategoryItemTracking({
      elementType: 'CategoriesSlider',
      header,
      pageComponentIndex,
      headerButtonLabel,
    });

  const items = useMemo(() => {
    return [
      ...placeholders.map((item, index) => (
        <Placeholder type={placeholderTypes.category} key={index} />
      )),
      ...categories.map((category, index) => {
        return (
          <CategoryItem
            key={category.id}
            category={category}
            componentType={componentType}
            onClick={() => trackCategoryClick(category, index)}
            skipMetadata={skipMetadata}
            size={displayLargeThumbnails ? 'large' : 'medium'}
          />
        );
      }),
    ];
  }, [
    categories,
    trackCategoryClick,
    placeholders,
    componentType,
    skipMetadata,
    displayLargeThumbnails,
  ]);

  const onHeaderButtonClick = useCallback(() => {
    trackHeaderButtonClick();

    setComponentPopupId(componentConfig.id);
  }, [componentConfig, setComponentPopupId]);

  if (isFetched && categories.length === 0) {
    return null;
  }

  const showHeaderButton =
    categories.length > Math.ceil(visibleItemsCount as number) &&
    Boolean(componentConfig.data.url);

  return (
    <Slider
      header={header}
      headerButtonLabel={showHeaderButton ? headerButtonLabel : undefined}
      onHeaderButtonClick={showHeaderButton ? onHeaderButtonClick : undefined}
      className={classNames(styles.slider, {
        [styles.large]: displayLargeThumbnails,
      })}
    >
      {items}
    </Slider>
  );
};

export const CategoriesSlider = memo(CategoriesSliderBase);
